import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { supabase } from '../../shared/supabase/supabaseClient';
import { reportAuthFailure } from '../application/authObservability';
import {
  clearPasswordRecoveryFlowMarker,
  markPasswordRecoveryFlowStarted,
} from '../application/passwordRecoveryWebStorage';
import { sessionIsPasswordRecovery } from '../application/passwordRecoverySession';

export type PasswordRecoveryGateStatus = 'inactive' | 'validating' | 'ready' | 'failed';

const VALIDATE_TIMEOUT_MS = 12_000;

function e2eForceReady(): boolean {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
  return Boolean((window as unknown as { __E2E_FORCE_PASSWORD_RECOVERY__?: boolean }).__E2E_FORCE_PASSWORD_RECOVERY__);
}

/**
 * Gate web para el form de nueva clave.
 * Solo `ready` con sesión de recovery real (evento PASSWORD_RECOVERY / amr),
 * no con “URL parece recovery + cualquier sesión”.
 *
 * `enabled` lo prende App cuando la URL/deep link es de recovery.
 */
export function usePasswordRecoveryGate(enabled: boolean): {
  status: PasswordRecoveryGateStatus;
  dismissFailed: () => void;
  completeRecoveryUi: () => void;
} {
  const [status, setStatus] = useState<PasswordRecoveryGateStatus>(() => {
    if (!enabled) return 'inactive';
    if (e2eForceReady()) return 'ready';
    return 'validating';
  });
  const statusRef = useRef(status);
  statusRef.current = status;

  const markReady = useCallback((session: Session) => {
    markPasswordRecoveryFlowStarted(session.user.id);
    setStatus('ready');
  }, []);

  const markFailed = useCallback(() => {
    if (statusRef.current === 'ready') return;
    clearPasswordRecoveryFlowMarker();
    setStatus('failed');
  }, []);

  const dismissFailed = useCallback(() => {
    clearPasswordRecoveryFlowMarker();
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      try {
        window.history.replaceState(null, '', `${window.location.origin}/`);
      } catch {
        /* ignore */
      }
    }
    setStatus('inactive');
  }, []);

  const completeRecoveryUi = useCallback(() => {
    setStatus('inactive');
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    if (!enabled) {
      setStatus('inactive');
      return;
    }

    if (e2eForceReady()) {
      setStatus('ready');
      return;
    }

    let cancelled = false;
    setStatus('validating');

    const considerSession = (event: AuthChangeEvent | 'manual', session: Session | null) => {
      if (cancelled) return;
      if (event === 'PASSWORD_RECOVERY' && session) {
        markReady(session);
        return;
      }
      if (session && sessionIsPasswordRecovery(session)) {
        markReady(session);
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      considerSession(event, session);
    });

    void (async () => {
      const qs = new URLSearchParams(window.location.search);
      const code = qs.get('code');
      // detectSessionInUrl puede ganar la carrera; reintentar exchange es best-effort.
      // Se reporta clasificado: verifier consumido (bad_code_verifier) → breadcrumb;
      // un 5xx real de GoTrue → issue.
      if (code) {
        const { error: exchangeErr } = await supabase.auth
          .exchangeCodeForSession(code)
          .catch((e) => ({ data: { session: null, user: null }, error: e }));
        if (exchangeErr) reportAuthFailure('reset', exchangeErr);
      }
      if (cancelled) return;
      const {
        data: { session },
      } = await supabase.auth.getSession();
      considerSession('manual', session);
    })();

    const timer = setTimeout(() => {
      if (cancelled) return;
      if (statusRef.current === 'validating') {
        markFailed();
      }
    }, VALIDATE_TIMEOUT_MS);

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, [enabled, markFailed, markReady]);

  return { status, dismissFailed, completeRecoveryUi };
}
